import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Plus, Search, Upload } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, ErrorState, PageHeader } from '@/components/page';
import { Pagination } from '@/components/pagination';
import { EntryStatusBadge, EntryTypeBadge } from '@/features/register/badges';
import { EntryActions } from '@/features/register/entry-actions';
import { FilterSelect } from '@/features/register/filter-select';
import { api } from '@/lib/api';
import { can, useMe } from '@/lib/auth';
import { DETAIL_LABEL, formatDate, PARTY_LABEL, sourceText } from '@/lib/labels';
import type { DetailType, Entry, EntryStatus, Page, Party } from '@/lib/types';

interface EntriesSearch {
  q?: string;
  party?: Party;
  detailType?: DetailType;
  status?: EntryStatus | 'ANY';
  page?: number;
}

const PARTIES = Object.keys(PARTY_LABEL) as Party[];
const DETAILS = Object.keys(DETAIL_LABEL) as DetailType[];

export const Route = createFileRoute('/_app/entries')({
  validateSearch: (s: Record<string, unknown>): EntriesSearch => ({
    q: typeof s.q === 'string' && s.q.trim() ? s.q : undefined,
    party: PARTIES.includes(s.party as Party) ? (s.party as Party) : undefined,
    detailType: DETAILS.includes(s.detailType as DetailType) ? (s.detailType as DetailType) : undefined,
    status: ['ACTIVE', 'ENDED', 'REMOVED', 'ANY'].includes(s.status as string) ? (s.status as EntriesSearch['status']) : undefined,
    page: Number(s.page) > 1 ? Math.floor(Number(s.page)) : undefined,
  }),
  component: EntriesPage,
});

const PAGE_SIZE = 25;

function EntriesPage() {
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [q, setQ] = useState(search.q ?? '');
  useEffect(() => setQ(search.q ?? ''), [search.q]);

  const status = search.status ?? 'ACTIVE';
  const query = useQuery({
    queryKey: ['entries', search],
    queryFn: () =>
      api.get<Page<Entry>>('/v1/entries', {
        q: search.q,
        party: search.party,
        detailType: search.detailType,
        status,
        page: search.page ?? 1,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const set = (patch: Partial<EntriesSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 'page' in patch ? patch.page : undefined }) });

  function submit(e: FormEvent) {
    e.preventDefault();
    set({ q: q.trim().length >= 2 ? q.trim() : undefined });
  }

  const admin = can.admin(me) && can.write(me);

  return (
    <>
      <PageHeader
        title="Register entries"
        description="Every detail held on the register. Searches and listings are audited."
        actions={
          can.write(me) ? (
            <>
              {admin ? (
                <Button variant="outline" asChild>
                  <Link to="/imports">
                    <Upload /> Upload backfill
                  </Link>
                </Button>
              ) : null}
              {can.record(me) ? (
                <Button asChild>
                  <Link to="/cases/new">
                    <Plus /> {admin ? 'New entry' : 'Record fraud'}
                  </Link>
                </Button>
              ) : null}
            </>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form role="search" onSubmit={submit} className="flex min-w-[260px] flex-1 gap-2 sm:max-w-md">
          <label htmlFor="entries-q" className="sr-only">
            Search entries
          </label>
          <Input id="entries-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any detail…" className="h-9" />
          <Button type="submit" variant="outline" size="sm" className="h-9">
            <Search /> Search
          </Button>
        </form>
        <FilterSelect label="Party" value={search.party} options={PARTIES.map((p) => ({ value: p, label: PARTY_LABEL[p] }))} onChange={(party) => set({ party })} />
        <FilterSelect label="Detail" value={search.detailType} options={DETAILS.map((d) => ({ value: d, label: DETAIL_LABEL[d] }))} onChange={(detailType) => set({ detailType })} />
        <FilterSelect
          label="Status"
          value={status}
          allLabel="Any status"
          options={[
            { value: 'ACTIVE', label: 'Active' },
            { value: 'ENDED', label: 'Ended' },
            { value: 'REMOVED', label: 'Removed' },
          ]}
          onChange={(v) => set({ status: v === 'ACTIVE' ? undefined : (v ?? 'ANY') })}
        />
      </div>

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted hover:bg-muted">
                <TableHead className="px-4">Detail</TableHead>
                <TableHead>Entry type</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Added</TableHead>
                {admin ? <TableHead className="w-12"><span className="sr-only">Actions</span></TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isPending
                ? Array.from({ length: 8 }, (_, i) => (
                    <TableRow key={i}>
                      <TableCell className="px-4" colSpan={admin ? 6 : 5}>
                        <Skeleton className="h-9 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                : query.data.items.map((e) => (
                    <TableRow key={e.id} className={e.status === 'REMOVED' ? 'opacity-70' : undefined}>
                      <TableCell className="max-w-[360px] px-4 py-2.5">
                        <div className="truncate font-mono text-[13px]" title={e.value}>
                          {e.value}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {PARTY_LABEL[e.party]} · {DETAIL_LABEL[e.detailType]}
                          {e.derived ? ' (derived)' : ''}
                        </div>
                      </TableCell>
                      <TableCell>
                        <EntryTypeBadge type={e.entryType} />
                      </TableCell>
                      <TableCell>
                        {e.fraudCase ? (
                          <Link to="/cases/$caseId" params={{ caseId: e.caseId }} className="text-sm text-link underline-offset-4 hover:underline">
                            {sourceText(e.fraudCase)}
                          </Link>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <EntryStatusBadge status={e.status} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(e.createdAt)}</TableCell>
                      {admin ? (
                        <TableCell className="pr-3 text-right">
                          <EntryActions entry={e} />
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
          {query.data && query.data.items.length === 0 ? (
            <div className="p-4">
              <EmptyState title={search.q ? `Nothing on the register matches “${search.q}”` : 'No entries yet'}>
                {search.q ? 'Try a different spelling, or include ended and removed entries using the Status filter.' : 'Entries appear here when fraud is recorded.'}
              </EmptyState>
            </div>
          ) : null}
          {query.data ? <Pagination page={query.data.page} pageSize={PAGE_SIZE} total={query.data.total} onPage={(page) => set({ page })} /> : null}
        </div>
      )}
    </>
  );
}
